import * as urlUtil from '@bingads-webui/url-util';
import { odata } from '@bingads-webui/http-util';
import _ from 'underscore';
import { INotification, TargetComponent, NotificationLevel, DismissMode, ScopeLevel, IContext } from '../../types';
import { i18nMap, shouldRender, isAvailable, isBillingLinkPermission } from '../../utils';

/**
 * ManualTest:
 * 1. Prepare fiddler mock data/real failed import
 * 2. Lauch site with fiddler opened
 * 3. Check the notificaiton in UNQ
 * 4. Go to import schedule page
 */
export interface IImportStatus {
  CurrentImportStatus: ImportStatusEnum;
  ImportId: number;
  ImportType: ImportTypeEnum;
  LastModifiedTime: number | string;
}

export enum ImportStatusEnum {
  StatusComplete = 6,
  StatusCancelled = 7,
  StatusFailed = 8,
  StatusUnknown = 9
}

export enum ImportTypeEnum {
  GoogleAPIImport = 1,
  FileBasedImport = 2,
  GoogleMyBusinessImport = 3,
  CloudImport = 4
}

export class ImportStatus {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
    this.availableScopes = [ScopeLevel.account];
  }

  async getImportStatusNotifications(scopeLevel?: ScopeLevel) {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    if (!isAvailable(this.context.getCurrentScope(), this.availableScopes)) {
      return [];
    }

    if (!shouldRender(scopeLevel || ScopeLevel.account, this.context.getChangeScope())) {
      return [];
    }

    const status: IImportStatus = await this.getImportStatus();
    const notifications: INotification[] = [];
    if (this.hasLastImportFailed(status) && this.isLastImportIn5Days(status)) {
      notifications.push(this.getLastImportFailedNotification());
    } else if (this.isImportPending(status)) {
      notifications.push(this.getImportPendingNotification());
    }

    return _.compact(notifications);
  }

  async getImportStatus(): Promise<IImportStatus> {
    const { aid } = this.context.getCurrentScope().values;
    const { cid } = this.context.getCurrentScope().values;

    const getImportStatusWithIdUrl = `/ODataApi/Campaign/V2/ImportSession/ImportStatus(CustomerId=${cid},AccountId=${aid})`;
    // pass import id 0 for fetching last import
    const queryParameters = {
      aid,
      cid,
      importId: 0,
    };
    const url = urlUtil.queryify(getImportStatusWithIdUrl, queryParameters);
    return (await odata.get({
      url,
      oDataToken: this.context.getAuthToken(),
    })) as IImportStatus;
  }

  getImportPendingNotification(): INotification {
    const { IsReadOnlyUser } = this.context.getCurrentPermissions();
    return {
      id: IsReadOnlyUser ? 'PendingImportInfoReadOnlyText' : 'PendingImportInfoText',
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PendingImportInfoText_Title),
      message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PendingImportInfoText_Message),
      actionList: IsReadOnlyUser
        ? []
        : [
          {
            text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_RestartTheImport),
            action: () => {
              this.redirectToImportCampaignSchedule();
            },
            callbackType: 'closeQueue',
            extraClassNames: ['HelpTopic'],
            extraAttrs: {
              query: 'help:app51001',
            },
          },
        ],
    };
  }

  getLastImportFailedNotification(): INotification {
    return {
      id: 'LastImportFailed',
      condition: true,
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_LastImportFailedInfoText_New_Title),
      message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_LastImportFailedInfoText_New_Message),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_SeeErrorDetails),
          action: () => {
            this.redirectToImportCampaignSchedule();
          },
          callbackType: 'closeQueue',
        },
      ],
    };
  }

  redirectToImportCampaignSchedule() {
    // $.advertiser.redirect('/Campaign/ImportCampaign/FileImport');
    // window.App.navigate('customer/:customer_id/account/:account_id/importschedule');
    this.context.route.goImportSchedule();
  }

  isImportStatusValid(status: IImportStatus) {
    return status && status.ImportId !== 0;
  }

  hasLastImportFailed(status: IImportStatus) {
    return this.isImportStatusValid(status) && status.CurrentImportStatus === ImportStatusEnum.StatusFailed;
  }

  hasLastImportBeenCancelled(status: IImportStatus) {
    return this.isImportStatusValid(status) && status.CurrentImportStatus === ImportStatusEnum.StatusCancelled;
  }

  isImportCompleted(status: IImportStatus) {
    return status.CurrentImportStatus === ImportStatusEnum.StatusComplete;
  }

  isImportPending(status: IImportStatus) {
    return (
      this.isImportStatusValid(status) &&
      !this.hasLastImportFailed(status) &&
      !this.hasLastImportBeenCancelled(status) &&
      !this.isImportCompleted(status)
    );
  }

  isLastImportIn5Days(status: IImportStatus) {
    return Date.now() - new Date(status.LastModifiedTime).getTime() < 24 * 60 * 60 * 1000 * 5;
  }
}
